import React, { useEffect, useMemo, useRef, useState } from "react";

import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { ColumnType } from "antd/es/table";
import moment from "moment";

import { getFarmDocumentScanById, getFieldShapeAdmin, listFarmDocumentScanFields, patchFarmDocumentScanField } from "services";

import { copyToClipboard, useParams } from "utils";

import { DEFAULT_CACHE_TIME, GET_DOC_SCAN, GET_DOC_SCAN_FIELDS, GET_FIELD_SHAPE_ADMIN, PATCH_DOC_SCAN_FIELD } from "consts";

import {
  Coordinates,
  CropMatchResult,
  DocFieldRaw,
  DocScanFieldRecord,
  DocScanFieldSortBy,
  DocScanJobDetail,
  DocScanSortOrder,
  FarmFieldRaw,
  RunContext,
  toDocScanField,
} from "models";

import {
  AdminHead,
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Icon,
  InputSearch,
  MainPage,
  MapBoundaryCompare,
  Modal,
  Option,
  OutlinedCard,
  Radio,
  Select,
  SourcePageViewer,
  Table,
  TagNormal,
  TextArea,
  TooltipWrapper,
  Typography,
  getDocFieldBoundaries,
  getDocFieldValues,
  Message as message,
  requiresDocFieldSplit,
} from "components";

import { DocScanSourceTag, DocScanTriggeredBy } from "pages/admin/verifications/DocScanOrigin";
import { DocScanStatisticsCard } from "pages/admin/verifications/DocScanStatisticsCard";
import { ComparisonRow, buildCropRows, buildFieldRows, getDocFieldSizeLabel } from "pages/admin/verifications/utils/docScanComparisonRows";

// ─── Status helpers ──────────────────────────────────────────────────────────

const STATUS_COLOR_MAP: Record<string, string> = {
  match: "green",
  matches: "green",
  mismatch: "red",
  mismatches: "red",
  new_data: "indigo",
  new: "indigo",
};

const getStatusColor = (status: string | undefined | null) => STATUS_COLOR_MAP[status ?? ""] ?? "grey-600";

const parseFieldLocation = (location: string): Coordinates | undefined => {
  try {
    return JSON.parse(location) as Coordinates;
  } catch {
    return undefined;
  }
};

const centroidOf = (points: Coordinates[]): Coordinates => {
  const sum = points.reduce((acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }), { lat: 0, lng: 0 });
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
};

// Small inline rendering of the actual polygon shapes (not just a generic icon) for the comparison table cells.
// All polygons share one bounding box so several parcels keep their relative position and size.
// Longitude is scaled by cos(latitude) so the shape isn't stretched east-west at higher latitudes.
const MiniPolygon: React.FC<{ polygons: Coordinates[][]; color: string; size?: number }> = ({ polygons, color, size = 48 }) => {
  const drawablePolygons = polygons.filter((polygon) => polygon.length >= 3);
  if (drawablePolygons.length === 0) return null;

  const points = drawablePolygons.flat();

  const avgLat = points.reduce((sum, p) => sum + p.lat, 0) / points.length;
  const lngScale = Math.cos((avgLat * Math.PI) / 180);
  const xs = points.map((p) => p.lng * lngScale);
  const ys = points.map((p) => -p.lat);

  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const span = Math.max(maxX - minX, maxY - minY) || 1;

  const padding = 3;
  const scale = (size - padding * 2) / span;
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;

  const toPath = (polygon: Coordinates[]) =>
    `${polygon
      .map((point, i) => {
        const x = size / 2 + (point.lng * lngScale - centerX) * scale;
        const y = size / 2 + (-point.lat - centerY) * scale;
        return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ")} Z`;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {drawablePolygons.map((polygon, index) => (
        <path key={index} d={toPath(polygon)} fill={color} fillOpacity={0.25} stroke={color} strokeWidth={1.5} />
      ))}
    </svg>
  );
};

const DB_VALUE_COLOR = "#1A73E8";
const DOC_VALUE_COLOR = "#FF8C00";

type DocScanFieldCardProps = {
  record: DocScanFieldRecord;
  index: number;
  scanId: string;
  onRecordUpdate: (updated: DocScanFieldRecord) => void;
  sourcePdfUrl?: string | null;
};

// ─── Column definitions ───────────────────────────────────────────────────────

const COMPARISON_COLUMNS: ColumnType<ComparisonRow>[] = [
  {
    title: "Item",
    dataIndex: "item",
    key: "item",
    width: "20%",
    render: (_: unknown, row: ComparisonRow) => {
      if (row.type === "map") {
        return (
          <Flex align="center" gap={6} className="pointer doc-scan-geometry-cell" onClick={row.onOpen}>
            <Typography variant="paragraph-s">Geometry</Typography>
            <Icon iconName="tabler/arrows-maximize" size="xs" />
          </Flex>
        );
      }
      if (row.type === "section") {
        const hasStatus = !!row.status;
        const hasScore = row.score !== undefined;
        return {
          children: (
            <Flex align="center" gap={8} className="doc-scan-section-label">
              <Typography variant="paragraph-s" bold className="grey-text">
                {row.label}
              </Typography>
              {(hasStatus || hasScore) && (
                <TagNormal
                  text={hasStatus && hasScore ? `${row.status}(${row.score})` : hasStatus ? row.status! : `(${row.score})`}
                  color={getStatusColor(row.status ?? null)}
                />
              )}
            </Flex>
          ),
          props: { colSpan: 4 },
        };
      }
      return row.item;
    },
  },
  {
    title: "DB Value",
    dataIndex: "dbValue",
    key: "dbValue",
    width: "25%",
    render: (_: unknown, row: ComparisonRow) => {
      if (row.type === "map") return row.platformGeometry?.length ? <MiniPolygon polygons={[row.platformGeometry]} color={DB_VALUE_COLOR} /> : "-";
      if (row.type !== "data") return { children: null, props: { colSpan: 0 } };
      return row.dbValue;
    },
  },
  {
    title: "Doc Value",
    dataIndex: "docValue",
    key: "docValue",
    width: "25%",
    render: (_: unknown, row: ComparisonRow) => {
      if (row.type === "map") return row.docGeometries?.length ? <MiniPolygon polygons={row.docGeometries} color={DOC_VALUE_COLOR} /> : "-";
      if (row.type !== "data") return { children: null, props: { colSpan: 0 } };
      return row.docValue;
    },
  },
  {
    title: "Status",
    key: "status",
    width: "30%",
    render: (_: unknown, row: ComparisonRow) => {
      if (row.type === "section") return { children: null, props: { colSpan: 0 } };
      const hasStatus = row.status != null && String(row.status).trim() !== "";
      const hasScore = row.score !== undefined && row.score !== null;
      if (!hasStatus && !hasScore) return "-";
      const text = hasStatus && hasScore ? `${row.status}(${row.score})` : hasStatus ? row.status : `(${row.score})`;
      return <TagNormal text={text} color={getStatusColor(row.status ?? null)} />;
    },
  },
];

const DocScanFieldCard: React.FC<DocScanFieldCardProps> = ({ record, index, scanId, onRecordUpdate, sourcePdfUrl }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [form] = Form.useForm();

  const field = toDocScanField(record);
  const dbField: FarmFieldRaw | undefined = field.farm_field_raw;
  const docField: DocFieldRaw | undefined = field.doc_field_raw;

  const docFieldName = getDocFieldValues(docField, (parcel) => parcel.field_name).join(", ");
  const cardKey = record.fieldId ?? (docFieldName || String(index));

  const differencesByAttribute = new Map<string, { db: string; doc: string }>();
  field.differences?.forEach((diff) => {
    differencesByAttribute.set(diff.attribute_name, { db: diff.db_value, doc: diff.doc_value });
  });

  const sizeDiff = differencesByAttribute.get("field_size") ?? differencesByAttribute.get("size");

  const docFieldSizeLabel = getDocFieldSizeLabel(docField, sizeDiff);

  const fieldName = docFieldName || dbField?.name || "Unknown Field";
  const fieldStatus = field.statuses?.overall_status ?? null;
  const fieldScore = field.scores?.field_score;
  const cropsScore = field.scores?.crops_score;

  const fieldCrops: CropMatchResult[] = field.crops ?? [];
  const groupedParcelCount = docField?.grouped_doc_fields?.length ?? 0;
  const needsSplit = requiresDocFieldSplit(docField);
  const sourceLocations = [...(docField?.source_locations ?? []), ...(docField?.crops ?? []).flatMap((crop) => crop.source_locations ?? [])];

  const { data: fieldShapeData } = useQuery({
    queryKey: [GET_FIELD_SHAPE_ADMIN, record.fieldId],
    queryFn: record.fieldId && isExpanded ? () => getFieldShapeAdmin({ fieldId: record.fieldId! }) : skipToken,
    gcTime: DEFAULT_CACHE_TIME,
    staleTime: DEFAULT_CACHE_TIME,
  });
  const fieldShape = fieldShapeData?.fieldShape;
  const cadastralGeometries = getDocFieldBoundaries(docField);
  const cadastralPoints = cadastralGeometries.flat();
  const dbFieldLocation = dbField?.location ? parseFieldLocation(dbField.location) : undefined;
  const cadastralCenter = cadastralPoints.length ? centroidOf(cadastralPoints) : undefined;
  const hasCadastralGeometry = cadastralGeometries.length > 0;
  const hasMaps = !!fieldShape || hasCadastralGeometry;
  const combinedGeometryPoints = [...(fieldShape?.geometry ?? []), ...cadastralPoints];
  const combinedCenter = combinedGeometryPoints.length ? centroidOf(combinedGeometryPoints) : dbFieldLocation ?? cadastralCenter;

  const tableRows: ComparisonRow[] = [
    ...(hasMaps
      ? [
          {
            type: "map",
            key: "map-row",
            platformGeometry: fieldShape?.geometry,
            docGeometries: cadastralGeometries,
            status: field.statuses?.geometry_status,
            score: field.scores?.geometry_score,
            onOpen: () => setShowMapModal(true),
          } as ComparisonRow,
        ]
      : []),
    ...buildFieldRows(dbField, docField, field.scores, field.statuses, docFieldSizeLabel),
    ...buildCropRows(docField, dbField, fieldCrops, cropsScore, field.statuses?.crops_status),
  ];

  const fullFieldId = record.fieldId ?? dbField?.id ?? "";
  const truncatedFieldId =
    fullFieldId && fullFieldId.includes("-")
      ? `${fullFieldId.split("-")[0]}...`
      : fullFieldId && fullFieldId.length > 8
      ? `${fullFieldId.slice(0, 8)}...`
      : fullFieldId || "-";

  const { mutate: submitReview, isPending: isSubmittingReview } = useMutation({
    mutationKey: [PATCH_DOC_SCAN_FIELD, record.id],
    mutationFn: (values: { isCorrect: boolean | null; comment: string }) =>
      patchFarmDocumentScanField({
        scanId,
        fieldId: record.id,
        isCorrect: values.isCorrect ?? null,
        comment: values.comment || null,
      }),
    onSuccess: (updated) => {
      onRecordUpdate(updated);
      setShowReviewModal(false);
      message({ content: "Review submitted successfully.", icon: "gremlin" });
    },
    onError: () => {
      message({ content: "There was an error submitting the review. Please try again.", icon: "gremlin" });
    },
  });

  // Set radio value when modal opens (comment is handled via TextArea initialValue)
  useEffect(() => {
    if (!showReviewModal) return;
    form.setFieldsValue({
      isCorrect: record.isCorrect == null ? undefined : String(record.isCorrect),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReviewModal]);

  const handleReviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReviewModal(true);
  };

  const handleSourceClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowSourceModal(true);
  };

  const handleReviewSubmit = (values: { isCorrect: string | undefined; comment: string }) => {
    submitReview({
      isCorrect: values.isCorrect === "true" ? true : values.isCorrect === "false" ? false : null,
      comment: values.comment,
    });
  };

  return (
    <OutlinedCard key={cardKey} className="margin-bottom-200">
      <Flex justify="space-between" align="center" className="margin-bottom-50 doc-scan-field-card-header" onClick={() => setIsExpanded((prev) => !prev)}>
        <Flex align="center" gap={8} style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "#06382b",
              flexShrink: 0,
            }}
          >
            <span style={{ color: "#fff", fontSize: "11px", fontWeight: 700, lineHeight: 1 }}>{index + 1}</span>
          </div>
          <div style={{ minWidth: 0, overflow: "hidden" }}>
            {fullFieldId ? (
              <TooltipWrapper tooltip={{ title: fullFieldId, placement: "top" }}>
                <Typography
                  variant="paragraph-m"
                  className="pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(fullFieldId);
                  }}
                >
                  <b>
                    <small>
                      {truncatedFieldId} <Icon iconName="tabler/copy" className="green-text pointer inline" size="s" />
                    </small>
                  </b>
                </Typography>
              </TooltipWrapper>
            ) : (
              <Typography variant="h4" noWrap>
                {fieldName}
              </Typography>
            )}
          </div>
        </Flex>
        <Flex gap={8} align="center" style={{ flexShrink: 0 }}>
          {groupedParcelCount > 0 && <TagNormal text={`${groupedParcelCount} parcels`} color="grey-600" style={{ flexShrink: 0, whiteSpace: "nowrap" }} />}
          {needsSplit && <TagNormal text="Needs split" color="orange" style={{ flexShrink: 0, whiteSpace: "nowrap" }} />}
          {(() => {
            const hasStatus = !!fieldStatus;
            const hasScore = fieldScore !== undefined;
            if (!hasStatus && !hasScore) return null;

            const text = hasStatus && hasScore ? `${fieldStatus}(${fieldScore})` : hasStatus ? fieldStatus : String(fieldScore);
            return <TagNormal text={text} color={getStatusColor(fieldStatus)} style={{ flexShrink: 0, whiteSpace: "nowrap" }} />;
          })()}
          {sourcePdfUrl && sourceLocations.length > 0 && (
            <Button type="secondary" size="small" onClick={handleSourceClick}>
              Source
            </Button>
          )}
          <Button type="secondary" size="small" onClick={handleReviewClick}>
            Review
          </Button>
          <Icon iconName={isExpanded ? "tabler/chevron-up" : "tabler/chevron-down"} size="m" className="doc-scan-field-card-chevron" />
        </Flex>
      </Flex>

      {isExpanded && (
        <>
          <Divider />
          <Table
            data={tableRows}
            columns={COMPARISON_COLUMNS as never}
            rowKey="key"
            pagination={false}
            className="doc-scan-comparison-table"
            rowClassName={(row: unknown) =>
              (row as ComparisonRow).type === "section" ? "doc-scan-section-row" : (row as ComparisonRow).type === "map" ? "doc-scan-map-row" : ""
            }
          />
        </>
      )}

      <Modal
        destroyOnClose
        wrapClassName="doc-scan-map-modal-wrap"
        showModal={showMapModal}
        onCancel={() => setShowMapModal(false)}
        maskClosable
        closeIcon={<Icon iconName="tabler/x" />}
        title="Field boundary comparison"
        width={1400}
        footer={
          <Button type="secondary" onClick={() => setShowMapModal(false)}>
            Close
          </Button>
        }
        content={
          <Flex vertical gap={12}>
            <div style={{ width: "100%", height: 600 }}>
              <MapBoundaryCompare
                primaryGeometry={fieldShape?.geometry ?? null}
                secondaryGeometries={cadastralGeometries}
                location={combinedCenter}
                primaryColor={DB_VALUE_COLOR}
                secondaryColor={DOC_VALUE_COLOR}
                zoomable
                pannable
                boundsPadding={24}
              />
            </div>
            <Flex gap={16}>
              <Flex align="center" gap={8}>
                <span style={{ width: 12, height: 12, background: DB_VALUE_COLOR, display: "inline-block" }} />
                <Typography variant="paragraph-s">DB value</Typography>
              </Flex>
              {hasCadastralGeometry && (
                <Flex align="center" gap={8}>
                  <span style={{ width: 12, height: 12, background: DOC_VALUE_COLOR, display: "inline-block" }} />
                  <Typography variant="paragraph-s">Doc value</Typography>
                </Flex>
              )}
            </Flex>
          </Flex>
        }
      />

      {sourcePdfUrl && (
        <Modal
          destroyOnClose
          showModal={showSourceModal}
          onCancel={() => setShowSourceModal(false)}
          maskClosable
          closeIcon={<Icon iconName="tabler/x" />}
          title="Source in document"
          wrapClassName="doc-scan-source-modal-wrap"
          footer={
            <Button type="secondary" onClick={() => setShowSourceModal(false)}>
              Close
            </Button>
          }
          content={<SourcePageViewer pdfUrl={sourcePdfUrl} locations={sourceLocations} />}
        />
      )}

      <Modal
        destroyOnClose
        wrapClassName="doc-scan-review-modal-wrap"
        showModal={showReviewModal}
        onCancel={() => setShowReviewModal(false)}
        closeIcon={<Icon iconName="tabler/x" />}
        title="Review Field Match"
        loading={isSubmittingReview}
        footer={
          <div className="doc-scan-create-footer">
            <Button type="secondary" onClick={() => setShowReviewModal(false)}>
              Cancel
            </Button>
            <Button type="primary" onClick={() => form.submit()} loading={isSubmittingReview}>
              Submit
            </Button>
          </div>
        }
        content={
          <Form form={form} onFinish={handleReviewSubmit} className="doc-scan-review-form" layout="vertical">
            <Form.Item name="isCorrect" label="Is this match correct?" style={{ marginBottom: 24 }}>
              <Radio.Group style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 35 }}>
                <Radio value="true">Yes, correct</Radio>
                <Radio value="false">No, incorrect</Radio>
              </Radio.Group>
            </Form.Item>
            <TextArea name="comment" label="Comment" rows={3} initialValue={record.comment ?? ""} />
          </Form>
        }
      />
    </OutlinedCard>
  );
};

// ─── Page component ───────────────────────────────────────────────────────────

const FIELD_STATUS_OPTIONS = ["ambiguous", "match", "mismatch", "missing_data", "new_data"] as const;

const FIELDS_LIMIT = 20;
const FIELDS_SORT_BY: DocScanFieldSortBy = "createdAt";
const FIELDS_SORT_ORDER: DocScanSortOrder = "asc";

export const DocScanDetails = () => {
  const { id } = useParams();

  const { data, isLoading, isError } = useQuery<DocScanJobDetail | null>({
    queryKey: [GET_DOC_SCAN, id],
    queryFn: id ? () => getFarmDocumentScanById({ id }) : () => Promise.resolve(null),
    enabled: !!id,
  });

  const isMatchType = data?.type === "match";

  const matchRunContext = isMatchType ? (data?.data as RunContext | null) : null;

  const sentinelRef = useRef<HTMLDivElement>(null);

  const [fieldStatusFilter, setFieldStatusFilter] = useState<string | null>(null);
  const [fieldSearch, setFieldSearch] = useState("");
  const [fieldsPage, setFieldsPage] = useState(1);
  const [loadedFields, setLoadedFields] = useState<DocScanFieldRecord[]>([]);
  const [hasMoreFields, setHasMoreFields] = useState(false);

  const { data: fieldsData, isFetching: fieldsFetching } = useQuery({
    queryKey: [GET_DOC_SCAN_FIELDS, id, fieldsPage, FIELDS_LIMIT, FIELDS_SORT_BY, FIELDS_SORT_ORDER, fieldStatusFilter],
    queryFn: () =>
      listFarmDocumentScanFields({
        id: id!,
        page: fieldsPage,
        limit: FIELDS_LIMIT,
        sortBy: FIELDS_SORT_BY,
        sortOrder: FIELDS_SORT_ORDER,
        overallStatus: fieldStatusFilter ?? undefined,
      }),
    enabled: !!id && isMatchType,
  });

  useEffect(() => {
    if (!fieldsData) return;
    setLoadedFields((prev) => (fieldsPage === 1 ? fieldsData.fields : [...prev, ...fieldsData.fields]));
    setHasMoreFields(fieldsData.count > fieldsPage * FIELDS_LIMIT);
  }, [fieldsData, fieldsPage]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreFields && !fieldsFetching) {
          setFieldsPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMoreFields, fieldsFetching]);

  const handleFieldStatusFilterChange = (value: string) => {
    setFieldStatusFilter(value === "" ? null : value);
    setFieldsPage(1);
    setLoadedFields([]);
  };

  const handleRecordUpdate = (updated: DocScanFieldRecord) => {
    setLoadedFields((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
  };

  const filteredFields = useMemo(() => {
    const trimmedSearch = fieldSearch.trim().toLowerCase();
    if (!trimmedSearch) return loadedFields;

    return loadedFields.filter((record) => {
      const dbField = record.farmFieldRaw;
      const docField = record.docFieldRaw;

      const searchableParts: string[] = [
        record.fieldId ?? "",
        dbField?.name ?? "",
        ...getDocFieldValues(docField, (parcel) => parcel.field_name),
        dbField?.flik ?? "",
        ...getDocFieldValues(docField, (parcel) => parcel.flik_number),
      ];

      const haystack = searchableParts
        .filter((part) => part && part.trim() !== "")
        .join(" ")
        .toLowerCase();

      if (!haystack) return false;

      return haystack.includes(trimmedSearch);
    });
  }, [loadedFields, fieldSearch]);

  const createdAt = data?.createdAt ? moment(data.createdAt).format("DD.MM.YYYY") : null;
  const completedAt = data?.completedAt ? moment(data.completedAt).format("DD.MM.YYYY") : null;

  return (
    <MainPage loading={isLoading} pageName="admin">
      <AdminHead headline="Doc-Scan details" description="Review the results of the document scan." />
      <div className="content">
        {isError && (
          <Typography variant="paragraph-m" className="margin-bottom-200">
            There was an error loading this doc-scan job.
          </Typography>
        )}

        {data && (
          <Card className="list-card outlined padding-150 margin-bottom-200">
            <Flex className="padding-bottom-100" align="center" justify="space-between">
              <Typography variant="h3">Job Overview</Typography>
            </Flex>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">Status</Typography>
                <Typography variant="paragraph-m" bold className="word-wrap">
                  {data.status}
                </Typography>
              </Flex>
              <hr />
            </div>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">Farm Id</Typography>
                <Typography variant="paragraph-m" bold className="word-wrap">
                  {data.farmId ?? "-"}
                </Typography>
              </Flex>
              <hr />
            </div>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">Source</Typography>
                <DocScanSourceTag source={data.source} />
              </Flex>
              <hr />
            </div>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">Triggered by</Typography>
                <DocScanTriggeredBy source={data.source} isAdmin={data.isAdmin} user={data.user} />
              </Flex>
              <hr />
            </div>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">Created</Typography>
                <Typography variant="paragraph-m" bold className="word-wrap">
                  {createdAt ?? "-"}
                </Typography>
              </Flex>
              <hr />
            </div>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">Completed</Typography>
                <Typography variant="paragraph-m" bold className="word-wrap">
                  {completedAt ?? "-"}
                </Typography>
              </Flex>
              <hr />
            </div>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">Attempts</Typography>
                <Typography variant="paragraph-m" bold>
                  {data.dispatchAttempts}/{data.maxDispatchAttempts}
                </Typography>
              </Flex>
              <hr />
            </div>
            <div>
              <Flex gap={64} justify="space-between">
                <Typography variant="paragraph-m">OCR engine</Typography>
                <Typography variant="paragraph-m" bold>
                  {matchRunContext?.ocr_engine ?? "-"}
                </Typography>
              </Flex>
              <hr />
            </div>
            {isMatchType && (
              <>
                <div>
                  <Flex gap={64} justify="space-between">
                    <Typography variant="paragraph-m">Overall Status</Typography>
                    <div>
                      {(() => {
                        const hasStatus = data.overallStatus != null && String(data.overallStatus).trim() !== "";
                        const hasScore = data.score != null;
                        if (!hasStatus && !hasScore)
                          return (
                            <Typography variant="paragraph-m" bold>
                              -
                            </Typography>
                          );
                        const text = hasStatus && hasScore ? `${data.overallStatus}(${data.score})` : hasStatus ? data.overallStatus! : `(${data.score})`;
                        return <TagNormal text={text} color={getStatusColor(data.overallStatus ?? null)} />;
                      })()}
                    </div>
                  </Flex>
                  <hr />
                </div>
                <div>
                  <Flex gap={64} justify="space-between">
                    <Typography variant="paragraph-m">Fields Score</Typography>
                    <Typography variant="paragraph-m" bold className="word-wrap">
                      {String(matchRunContext?.fields_score ?? "-")}
                    </Typography>
                  </Flex>
                  <hr />
                </div>
                <div>
                  <Flex gap={64} justify="space-between">
                    <Typography variant="paragraph-m">Document Year</Typography>
                    <Typography variant="paragraph-m" bold className="word-wrap">
                      {String(data.year ?? "-")}
                    </Typography>
                  </Flex>
                  <hr />
                </div>
                <div>
                  <Flex gap={64} justify="space-between">
                    <Typography variant="paragraph-m">Region</Typography>
                    <TagNormal
                      text={
                        matchRunContext?.region_score !== undefined
                          ? `${matchRunContext.region_status ?? "-"}(${matchRunContext.region_score})`
                          : matchRunContext?.region_status ?? "-"
                      }
                      color={getStatusColor(matchRunContext?.region_status ?? null)}
                    />
                  </Flex>
                  <hr />
                </div>
                <div>
                  <Flex gap={64} justify="space-between">
                    <Typography variant="paragraph-m">Address</Typography>
                    <TagNormal
                      text={
                        matchRunContext?.address_score !== undefined
                          ? `${matchRunContext.address_status ?? "-"}(${matchRunContext.address_score})`
                          : matchRunContext?.address_status ?? "-"
                      }
                      color={getStatusColor(matchRunContext?.address_status ?? null)}
                    />
                  </Flex>
                  <hr />
                </div>
                <Flex gap={64} justify="space-between">
                  <Typography variant="paragraph-m">Summary</Typography>
                  <Typography variant="paragraph-m" bold className="word-wrap">
                    {matchRunContext?.summary ?? "-"}
                  </Typography>
                </Flex>
              </>
            )}
          </Card>
        )}

        {isMatchType && data?.statistics && <DocScanStatisticsCard statistics={data.statistics} />}

        {!isLoading && !data?.data && (
          <Typography variant="paragraph-m" className="margin-bottom-200">
            This doc-scan job has no comparison data attached. Only the basic job details are available.
          </Typography>
        )}

        {isMatchType && !isLoading && data?.data && (
          <>
            <Flex justify="space-between" align="center" className="margin-bottom-200">
              <div>
                <Typography variant="h3">Fields</Typography>
                {fieldsData?.count != null && (
                  <Typography variant="paragraph-s" className="grey-text">
                    Total fields: {fieldsData.count}
                  </Typography>
                )}
              </div>
              <Flex align="center" gap={16}>
                <InputSearch
                  label="Search by field id, name or FLIK"
                  value={fieldSearch}
                  onChange={(e) => setFieldSearch(e.target.value)}
                  style={{ minWidth: 260 }}
                  isSmallVariant
                />
                {
                  <Select
                    className="doc-scan-fields-filter-select"
                    value={fieldStatusFilter ?? ""}
                    onChange={handleFieldStatusFilterChange}
                    style={{ minWidth: 180 }}
                  >
                    <Option value="">All</Option>
                    {FIELD_STATUS_OPTIONS.map((status) => (
                      <Option key={status} value={status}>
                        {status}
                      </Option>
                    ))}
                  </Select>
                }
              </Flex>
            </Flex>
          </>
        )}

        {isMatchType &&
          filteredFields.map((record, index) => (
            <DocScanFieldCard key={record.id} record={record} index={index} scanId={id!} onRecordUpdate={handleRecordUpdate} sourcePdfUrl={data?.fileUrl} />
          ))}

        {isMatchType && <div ref={sentinelRef} style={{ height: 1 }} />}
      </div>
    </MainPage>
  );
};
